import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatPLN, formatDate } from "../lib/api";
import { CheckCircle2 } from "lucide-react";

export default function CompletedOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/orders", { params: { status: "completed" } })
      .then((r) => setOrders(r.data))
      .finally(() => setLoading(false));
  }, []);

  const totals = useMemo(() => {
    let revenue = 0, costs = 0, profit = 0;
    orders.forEach((o) => {
      revenue += Number(o.sale_price || 0);
      costs += Number(o.total_costs || 0);
      profit += Number(o.profit || 0);
    });
    return { revenue, costs, profit };
  }, [orders]);

  return (
    <div className="space-y-6" data-testid="completed-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2">
          Archiwum
        </div>
        <h1 className="heading text-3xl sm:text-4xl font-semibold tracking-tight">
          Zakończone zlecenia
        </h1>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="completed-totals">
        <Tot label="Przychód" value={formatPLN(totals.revenue)} />
        <Tot label="Koszty" value={formatPLN(totals.costs)} />
        <Tot
          label="Zarobek na czysto"
          value={formatPLN(totals.profit)}
          accent="var(--success)"
        />
      </div>

      {loading ? (
        <div className="surface p-10 text-center text-[var(--text-muted)]">Wczytywanie...</div>
      ) : orders.length === 0 ? (
        <div className="surface p-12 text-center" data-testid="completed-empty">
          <div className="w-14 h-14 rounded-full bg-[var(--surface-2)] mx-auto mb-4 flex items-center justify-center">
            <CheckCircle2 className="text-[var(--success)]" size={22} />
          </div>
          <div className="heading text-lg font-semibold">Brak zakończonych zleceń</div>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Po zakończeniu pierwszego zlecenia pojawi się ono tutaj.
          </p>
        </div>
      ) : (
        <div className="surface overflow-hidden" data-testid="completed-table">
          <div className="grid grid-cols-12 px-5 py-3 text-xs uppercase tracking-wider text-[var(--text-muted)] border-b border-[var(--border)] bg-[var(--surface-2)]/40">
            <div className="col-span-4">Nazwa</div>
            <div className="col-span-3">Klient</div>
            <div className="col-span-2 text-right">Cena</div>
            <div className="col-span-2 text-right">Koszty</div>
            <div className="col-span-1 text-right">Zarobek</div>
          </div>
          {orders.map((o) => (
            <Link
              key={o.id}
              to={`/zlecenia/${o.id}`}
              data-testid={`completed-row-${o.id}`}
              className="grid grid-cols-12 px-5 py-3 items-center border-b last:border-0 border-[var(--border)] hover:bg-[var(--surface-2)]/40 transition-colors"
            >
              <div className="col-span-4">
                <div className="font-medium">{o.name}</div>
                <div className="text-xs text-[var(--text-muted)]">
                  {formatDate(o.completed_at || o.created_at)}
                </div>
              </div>
              <div className="col-span-3 text-sm text-[var(--text-muted)]">
                {o.client_name || "—"}
                {o.client_phone && <span className="block text-xs">{o.client_phone}</span>}
              </div>
              <div className="col-span-2 text-right tabular-nums">
                {formatPLN(o.sale_price)}
              </div>
              <div className="col-span-2 text-right tabular-nums">
                {formatPLN(o.total_costs)}
              </div>
              <div
                className="col-span-1 text-right tabular-nums font-semibold"
                style={{
                  color: Number(o.profit) >= 0 ? "var(--success)" : "var(--danger)",
                }}
              >
                {formatPLN(o.profit)}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Tot({ label, value, accent }) {
  return (
    <div className="surface p-5">
      <div className="text-xs text-[var(--text-muted)] uppercase tracking-wide">
        {label}
      </div>
      <div
        className="heading text-2xl font-bold mt-2 tabular-nums"
        style={accent ? { color: accent } : {}}
      >
        {value}
      </div>
    </div>
  );
}
